/**
 * @module Experience/Plan-Details
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */
/* tslint:disable:no-any */ // Need to support all data types
// CORE
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

// API
import { API_MAP } from '@core/api/proxies/common-api-endpoints';

// SERVICES
import { ApiGatewayService } from '@core/api/services/api-gateway-service';
import { ContentProviderService } from '@core/aem/services/content-provider.service';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { CartSupportService } from '@support/cart-support/services/cart-support.service';

// MODEL
import { PlanDetailsRequestModel } from '@experience/shop/plan-details/models/plan-details-request.model';

// INTERFACES
import { PlanDetailResponse } from '@support/api/proxies/responses/api-2123.interface';
import { BrowseEntry } from '@experience/shop/shared-browse/interfaces/browse-entry.interface';
import { Cart, CartResponse } from '@support/api/proxies/responses/api-cart-response.interface';
import { BrowsePlanConfig } from '@core/aem/interfaces/shop/browse-plan.interface';
// CONSTANTS
import { PLAN_DETAILS_CONSTANTS } from '@experience/shop/plan-details/constants/product-details-constant';
import { PlanDetailsResponseModel } from '@experience/shop/plan-details/models/plan-details-response.model';
import { PlanOption } from '@support/api/proxies/responses/api-2117.interface';
import { map } from 'rxjs/operators';
import { HttpResponse } from '@angular/common/http';
import { SHARED_BROWSE_CONSTANTS } from '@experience/shop/shared-browse/constants/shared-browse.constants';
@Injectable({
  providedIn: 'root',
})
export class PlanDetailsService {
  /**
   * Subject for the planOption
   */
  public selectedPlanSubject: Subject<String>;

  /**
   * Holds the value of selected Plan as an Observable
   */
  public selectedPlan$: Observable<String>;

  /**
   * Holds new Plan
   */
  public newPlan: PlanOption;

  constructor(
    private contentService: ContentProviderService,
    private apiGateway: ApiGatewayService,
    private stateStoreUtilsService: StateStoreUtilsService,
    private objectService: ObjectService,
    private cartSupportService: CartSupportService
  ) {
    this.selectedPlanSubject = new Subject();
    this.selectedPlan$ = this.selectedPlanSubject.asObservable();
  }

  /**
   * Get AEM content data
   * @returns BrowsePlanConfig
   */
  public getAemContent(): Observable<BrowsePlanConfig> {
    return this.contentService
      .getContentData(PLAN_DETAILS_CONSTANTS.PLAN_DETAILS, PLAN_DETAILS_CONSTANTS.BILLING_TYPE)
      .pipe(
        map((contentData: BrowsePlanConfig) => {
          return <BrowsePlanConfig>this.contentService.getConfigurableData(contentData);
        })
      );
  }

  /**
   * Retrieves plan-product-details
   * @returns PlanDetailsResponse
   */
  public initApiData(selectedFamilyId: string, lineNumber: number): Observable<PlanDetailResponse> {
    const pathParams = {
      familyId: selectedFamilyId,
    };
    const customerConfigData = this.stateStoreUtilsService.getCustomerConfigData();
    const billingType = this.stateStoreUtilsService.getCustomerBillingType();
    const customerId = this.stateStoreUtilsService.getCustomerId();
    const postalCode =
      this.objectService.get(this.stateStoreUtilsService.getBillingAddress(), 'postalCode') ||
      customerConfigData.zip;
    const storeId = this.stateStoreUtilsService.getStoreId();
    const accountId = this.stateStoreUtilsService.getAddFan()
      ? ''
      : this.stateStoreUtilsService.getAccountId();
    const planDetailsRequestPayload = new PlanDetailsRequestModel(
      billingType,
      customerId,
      postalCode,
      storeId,
      selectedFamilyId,
      accountId,
      lineNumber
    );
    return this.apiGateway
      .postRequest(
        API_MAP.API_2148_retrieveProductPlansPdp,
        planDetailsRequestPayload,
        null,
        null,
        pathParams
      )
      .pipe(
        map((response: HttpResponse<PlanDetailResponse>) => {
          return response.body;
        })
      );
  }

  /**
   * Gets saved user input data
   * @returns BrowseEntry from stateStoreService or default data
   */
  public getUserInputsConfigData(): BrowseEntry {
    return this.stateStoreUtilsService.getCustomerConfigData();
  }

  /**
   * Gets selected plan Id
   *
   */
  public getSelectedPlan(): string {
    return this.stateStoreUtilsService.getPlansPDPSelectedPlanId();
  }

  /**
   * Gets line in focus
   * @returns string
   */
  public getLineId(): string {
    return this.stateStoreUtilsService.getLineId();
  }

  /**
   * Set Updated Plan
   * @returns string
   */
  public setUpdatedPlan(plan: PlanOption): void {
    this.newPlan = plan;
  }

  /**
   * Set New Plan
   * @returns void
   */
  public setNewPlan(): void {
    if (
      this.stateStoreUtilsService.getActivePlanForLine() !==
      this.objectService.get(this.newPlan, 'planId')
    ) {
      this.stateStoreUtilsService.setNewPlanForLine(this.newPlan);
    }
  }

  /**
   * Gets pervious url
   *
   */
  public getPreviousState(): { isCPS: boolean } {
    return this.stateStoreUtilsService.getPlansPDPPreviousState();
  }

  /**
   * Sets model in state store
   * @param familyDetailModel: PlanDetailsResponseModel
   */
  public setModelInStateStore(familyDetailModel: PlanDetailsResponseModel): void {
    this.stateStoreUtilsService.setPlanFamilyDetails(familyDetailModel);
  }

  /**
   * Gets cart data on click of change existing plan button
   * @return Observable<CartResponse>
   */
  public putSelectedPlanToCart(
    selectedPlanId: string,
    lineNumber: string,
    isProductSelector: boolean,
    transactionTypes: string,
    accountId: string,
    overrrideIndicator?: boolean
  ): Observable<CartResponse> {
    return this.cartSupportService.putSelectedPlanToCart(
      selectedPlanId,
      lineNumber,
      true,
      transactionTypes,
      accountId,
      null,
      overrrideIndicator
    );
  }

  public updateCartLevelDeviceToLineLevel(
    paramDeviceId: string,
    paramSku: string,
    lineId?: string
  ): Observable<Cart> {
    return this.cartSupportService.updateCartLevelDeviceToLineLevel(
      paramDeviceId,
      paramSku,
      lineId
    );
  }
  /**
   * Sets selected plan
   * @param planId: string
   */
  public setSelectedPlan(planId: string): void {
    this.stateStoreUtilsService.setPlansPDPSelectedPlanId(planId);
  }
}
